import React, { useState, useEffect } from "react";
import styles from "./Dashboard.module.scss";
import moment from "moment";
import { db } from "../../firebase";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { Modal, Dropdown, Menu, Table, Spin, DatePicker, Button } from "antd";
import { EllipsisOutlined } from '@ant-design/icons';
import { LoadingOutlined } from "@ant-design/icons";
import { FaEye } from "react-icons/fa";
import { useAuth } from "../../Context/AuthContext";
import { toast } from "react-toastify";

export default function ActivityFeed() {
  const [activityFeed, setActivityFeed] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [bountyData, setBountyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [activitySummary, setActivitySummary] = useState(null);
  const [bountyModalVisible, setBountyModalVisible] = useState(false); 
  const [activityTypes, setActivityTypes] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [bountyLoading, setBountyLoading] = useState(false); 
  const [viewModalData, setViewModalData] = useState(null);
  const [requestLoading, setRequestLoading] = useState(false);

  const [quickSummaryData, setQuickSummaryData] = useState(null);
  const [quickSummaryModalVisible, setQuickSummaryModalVisible] = useState(false);
  const [loadingQuickSummary, setLoadingQuickSummary] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [selectedRangeType, setSelectedRangeType] = useState("currentWeek");
  const [rangeHeaders, setRangeHeaders] = useState({});
  
  // Activity Tracker Summary date range states
  const [activityDateRange, setActivityDateRange] = useState(null);
  const [activitySelectedRangeType, setActivitySelectedRangeType] = useState("currentWeek");
  const [activityRangeHeaders, setActivityRangeHeaders] = useState({});
  
  const { userInfo } = useAuth();

  // attempt to pull id from player profile
  const [playerIdMap, setPlayerIdMap] = useState({});
  
  
  const [descriptionModalData, setDescriptionModalData] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let isMounted = true;
  
    const fetchMissingPlayerIds = async () => {
      const newMap = { ...playerIdMap };
      let hasUpdates = false;
  
      for (const item of activityFeed) {
        if (!item.playerID && item.player) {
          const playerName = item.player;
          if (!(playerName in newMap)) {
            try {
              const querySnapshot = await db.collection("player-profile")
                .where("Title", "==", playerName)
                .limit(1)
                .get();
  
              if (!isMounted) return;
  
              if (!querySnapshot.empty) {
                newMap[playerName] = querySnapshot.docs[0].id;
              } else {
                newMap[playerName] = null;
              }
              hasUpdates = true;
            } catch (error) {
              console.error("Error fetching player ID:", error);
              newMap[playerName] = null;
              hasUpdates = true;
            }
          }
        }
      }
  
      if (hasUpdates && isMounted) {
        setPlayerIdMap(newMap);
      }
    };
  
    fetchMissingPlayerIds();
  
    return () => {
      isMounted = false;
    };
  }, [activityFeed]); // eslint-disable-line react-hooks/exhaustive-deps

  const { RangePicker } = DatePicker;

  const getPresetRanges = () => {
    return {
      "Today": [
        moment().startOf("day"),
        moment().endOf("day")
      ],
      "Yesterday": [
        moment().subtract(1, "day").startOf("day"),
        moment().subtract(1, "day").endOf("day")
      ],
      "Current Week": [
        moment().startOf("isoWeek"), // Changed from "week" to "isoWeek" for Monday-Sunday
        moment().endOf("isoWeek")
      ],
      "Current Month": [
        moment().startOf("month"),
        moment().endOf("month")
      ],
      "Previous Month": [
        moment().subtract(1, "month").startOf("month"),
        moment().subtract(1, "month").endOf("month")
      ],
      "Last 7 Days": [
        moment().subtract(7, "days").startOf("day"),
        moment().endOf("day")
      ],
      "Last 365 Days": [
        moment().subtract(365, "days").startOf("day"),
        moment().endOf("day")
      ],
      "Year To Date": [
        moment().startOf("year"),
        moment().endOf("day")
      ]
    };
  };

  const getRangeHeaders = (range, presetName = null) => {
    if (!range || !range[0] || !range[1]) {
      return { primary: "Current Week Breakdown" };
    }
    
    // If it's a preset range, use the preset name for the header
    if (presetName) {
      return { primary: `${presetName} Breakdown` };
    }
    
    // Use the moment objects directly instead of creating new ones
    const start = range[0];
    const end = range[1];
    
    const daysDiff = end.diff(start, "days");
    
    // For single day ranges (Today, Yesterday), show just one date
    if (daysDiff === 0) {
      return { primary: `${start.format("MMM D, YYYY")} Breakdown` };
    }
    
    // For multi-day ranges, always show the full range
    return { primary: `${start.format("MMM D")} - ${end.format("MMM D")} Breakdown` };
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      setSelectedRangeType("custom");
      
      // Convert dayjs objects to moment objects properly
      const momentDates = [moment(dates[0].toDate()), moment(dates[1].toDate())];
      
      const headers = getRangeHeaders(momentDates);
      setRangeHeaders(headers);
      fetchQuickSummary(momentDates);
    }
  };

  const handlePresetClick = (presetName) => {
    if (presetName === "Custom Range") {
      setSelectedRangeType("custom");
      setDateRange(null); // Clear the range to show the picker
      return;
    }
    
    const ranges = getPresetRanges();
    const range = ranges[presetName];
    setDateRange(range);
    setSelectedRangeType(presetName.toLowerCase().replace(" ", ""));
    setRangeHeaders(getRangeHeaders(range, presetName));
    fetchQuickSummary(range);
  };

  // Activity Tracker Summary date range handlers
  const handleActivityDateRangeChange = (dates) => {
    setActivityDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      setActivitySelectedRangeType("custom");
      
      // Convert dayjs objects to moment objects properly
      const momentDates = [moment(dates[0].toDate()), moment(dates[1].toDate())];
      
      const headers = getRangeHeaders(momentDates);
      setActivityRangeHeaders(headers);
      fetchActivitySummary(momentDates);
    }
  };

  const handleActivityPresetClick = (presetName) => {
    if (presetName === "Custom Range") {
      setActivitySelectedRangeType("custom");
      setActivityDateRange(null); // Clear the range to show the picker
      return;
    }
    
    const ranges = getPresetRanges();
    const range = ranges[presetName];
    setActivityDateRange(range);
    setActivitySelectedRangeType(presetName.toLowerCase().replace(" ", ""));
    setActivityRangeHeaders(getRangeHeaders(range, presetName));
    fetchActivitySummary(range);
  };

  const fetchQuickSummary = (customRange = null) => {
    setQuickSummaryModalVisible(true);
    setLoadingQuickSummary(true);
    
    // Use custom range if provided, otherwise use current dateRange state
    const range = customRange || dateRange;
    
    // Determine date ranges based on selection
    let primaryStart, primaryEnd;
    
    if (range && range[0] && range[1]) {
      // Ensure we're working with proper moment objects
      primaryStart = moment(range[0]).startOf('day');
      primaryEnd = moment(range[1]).endOf('day');
    } else {
      // Default to current week
      primaryStart = moment().startOf("isoWeek");
      primaryEnd = moment().endOf("isoWeek");
    }
    
    // Convert moment objects to Unix timestamps for Firestore query
    const startTimestamp = primaryStart.unix();
    const endTimestamp = primaryEnd.unix();
    
    // Query Firestore with timestamp range instead of fetching all
    db.collection("activity")
      .where("timestamp", ">=", startTimestamp)
      .where("timestamp", "<=", endTimestamp)
      .orderBy("timestamp", "desc")
      .get()
      .then((snapshot) => {
        const summary = {
          primary: {
            profilesAdded: 0,
            profilesEdited: 0,
            scoutingReportsAdded: 0,
            scoutingReportsEdited: 0,
            intelReportsAdded: 0,
            intelReportsEdited: 0,
            medicalReportsAdded: 0,
            medicalReportsEdited: 0,
            generalReportsAdded: 0,
            generalReportsEdited: 0,
            backgroundOverviewsAdded: 0,
            backgroundOverviewsEdited: 0,
            scoutingOverviewsAdded: 0,
            scoutingOverviewsEdited: 0,
            piScoresAdded: 0,
            piScoresEdited: 0,
            totalAdditions: 0,
            totalEdits: 0,
          }
        };
        
        let activityCount = 0;
        let matchedActivities = [];
        
        snapshot.forEach(doc => {
          const data = doc.data();
          const activityDate = moment.unix(data.timestamp);
          
          updateCounts(summary.primary, data.desc);
          activityCount++;
          matchedActivities.push({
            date: activityDate.format("YYYY-MM-DD"),
            desc: data.desc
          });
        });
        
        // Calculate totals for primary
        summary.primary.totalAdditions = 
          summary.primary.profilesAdded +
          summary.primary.scoutingReportsAdded +
          summary.primary.intelReportsAdded +
          summary.primary.medicalReportsAdded +
          summary.primary.generalReportsAdded +
          summary.primary.backgroundOverviewsAdded +
          summary.primary.scoutingOverviewsAdded +
          summary.primary.piScoresAdded;
        
        summary.primary.totalEdits = 
          summary.primary.profilesEdited +
          summary.primary.scoutingReportsEdited +
          summary.primary.intelReportsEdited +
          summary.primary.medicalReportsEdited +
          summary.primary.generalReportsEdited +
          summary.primary.backgroundOverviewsEdited +
          summary.primary.scoutingOverviewsEdited +
          summary.primary.piScoresEdited;
        
        setQuickSummaryData(summary);
        setLoadingQuickSummary(false);
      }).catch(error => {
        console.error("Error fetching quick summary:", error);
        setLoadingQuickSummary(false);
      });
  };
  
const mapActivityDescForSummary = (desc = "") => {
  const normalized = desc.trim().toLowerCase();
  // Contact add/edit are updates to an existing player — count under Edited Player Profile
  if (normalized === "player contact added") return "player profile was edited";
  if (normalized === "player contact edited") return "player profile was edited";
  return normalized;
};

const updateCounts = (periodData, desc) => {
  const normalized = mapActivityDescForSummary(desc);

  switch(normalized) {
    case 'new player profile added':
      periodData.profilesAdded++; break;
    case 'player profile was edited':
      periodData.profilesEdited++; break;
    case 'new scouting report added':
      periodData.scoutingReportsAdded++; break;
    case 'scouting report was edited':
      periodData.scoutingReportsEdited++; break;
    case 'new intel report added':
      periodData.intelReportsAdded++; break;
    case 'intel report was edited':
      periodData.intelReportsEdited++; break;
    case 'new medical report added':
      periodData.medicalReportsAdded++; break;
    case 'medical report was edited':
      periodData.medicalReportsEdited++; break;
    case 'new general report added':
      periodData.generalReportsAdded++; break;
    case 'general report was edited':
      periodData.generalReportsEdited++; break;
    case 'new background overview added':
      periodData.backgroundOverviewsAdded++; break;
    case 'background overview was edited':
      periodData.backgroundOverviewsEdited++; break;
    case 'scouting overview added':
      periodData.scoutingOverviewsAdded++; break;
    case 'scouting overview was edited':
      periodData.scoutingOverviewsEdited++; break;
    case 'new pi score added':
      periodData.piScoresAdded++; break;
    case 'pi score edited':
      periodData.piScoresEdited++; break;
    default:
      break;
  }
};
  

  const getActivityFeed = (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    let query = db.collection("activity")
      .orderBy("timestamp", "desc")
      .limit(20);

    if (loadMore && lastVisible) {
      query = query.startAfter(lastVisible);
    }

    query.get().then((snapshot) => {
      const feed = snapshot.docs.map((doc) => ({ ...doc.data() }));
      const lastDoc = snapshot.docs[snapshot.docs.length - 1];

      setActivityFeed((prevFeed) => loadMore ? [...prevFeed, ...feed] : feed);
      setLastVisible(lastDoc);
      setHasMore(snapshot.docs.length === 20);
      setLoading(false);
      setLoadingMore(false);
    });
  };

  const handleBountyBoardOpen = () => {
    fetchBounties(); // Fetch bounties when the modal opens
    setBountyModalVisible(true);
  };

  const fetchBounties = async () => {
    setLoading(true);
    try {
      const response = await db.collection("bounties").get();
      const data = response.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
  
      const sortedData = data.sort((a, b) => {
        const progressOrder = {
          "Not Started": 1,
          "In Progress": 2,
          Completed: 3,
        };
  
        if (progressOrder[a.progress] !== progressOrder[b.progress]) {
          return progressOrder[a.progress] - progressOrder[b.progress];
        }
  
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateA - dateB;
      });
  
      setBountyData(sortedData);
    } catch (error) {
      console.error("Error fetching bounties: ", error);
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    getActivityFeed();
  }, []);

  const loadMore = () => {
    if (hasMore) {
      getActivityFeed(true);
    }
  };

  const fetchActivitySummary = (customRange = null) => {
    setModalVisible(true);
    setLoadingSummary(true);

    let query = db.collection("activity");
    
    // If no custom range provided, use current week as default
    let range = customRange;
    if (!range) {
      const ranges = getPresetRanges();
      range = ranges["Current Week"];
      setActivityDateRange(range);
      setActivitySelectedRangeType("currentweek");
      setActivityRangeHeaders(getRangeHeaders(range, "Current Week"));
    }
    
    // Apply date range filter
    if (range && range[0] && range[1]) {
      const startTimestamp = range[0].startOf('day').unix();
      const endTimestamp = range[1].endOf('day').unix();
      query = query.where('timestamp', '>=', startTimestamp).where('timestamp', '<=', endTimestamp);
    }

    query
      .get()
      .then((snapshot) => {
        console.log('Activity Summary Query Results:', snapshot.size, 'documents');
        const summary = {};
        const types = new Set();
        snapshot.forEach((doc) => {
          const data = doc.data();
          const normalizedEditor = data.editor === "Mikey Visenberg" ? "Michael Visenberg" : data.editor;
          const summaryDesc = mapActivityDescForSummary(data.desc);
          const displayDesc = summaryDesc === "new player profile added"
            ? "New Player Profile Added"
            : summaryDesc === "player profile was edited"
              ? "Player Profile was Edited"
              : data.desc;
          types.add(displayDesc);

          if (!summary[normalizedEditor]) {
            summary[normalizedEditor] = {};
          }
          if (!summary[normalizedEditor][displayDesc]) {
            summary[normalizedEditor][displayDesc] = 0;
          }

          summary[normalizedEditor][displayDesc]++;
        });

        console.log('Activity Summary Data:', summary);
        console.log('Activity Types:', Array.from(types));
        setActivitySummary(summary);
        setActivityTypes(Array.from(types));
      })
      .catch((error) => {
        console.error('Error fetching activity summary:', error);
        toast.error('Error loading activity summary');
      })
      .finally(() => {
        setLoadingSummary(false);
      });
  };

  const activityTableData = activitySummary
    ? Object.keys(activitySummary).map((editor) => {
        const editorData = { key: editor, editor };
        let totalEdits = 0;
        activityTypes.forEach((type) => {
          editorData[type] = activitySummary[editor][type] || 0;
          totalEdits += editorData[type];
        });
        editorData.totalEdits = totalEdits;
        return editorData;
      }).sort((a, b) => b.totalEdits - a.totalEdits)
    : [];

  // Ensure all activity types are represented in the totals row
  const totalRow = activityTableData.reduce((totals, row) => {
    activityTypes.forEach((type) => {
      totals[type] = (totals[type] || 0) + (row[type] || 0);
    });
    totals.totalEdits = (totals.totalEdits || 0) + (row.totalEdits || 0);
    return totals;
  }, { editor: "Total", key: "Total" });

  // Add totals row to the data
  const finalActivityTableData = [...activityTableData, totalRow];

  console.log('Activity Table Data:', activityTableData);
  console.log('Activity Summary State:', activitySummary);
  console.log('Activity Types State:', activityTypes);

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) {
      return finalActivityTableData;
    }
  
    // Exclude totals row from sorting
    const totalsRowIndex = finalActivityTableData.findIndex((row) => row.editor === "Total");
    const rowsWithoutTotal = totalsRowIndex === -1 ? finalActivityTableData : finalActivityTableData.slice(0, totalsRowIndex).concat(finalActivityTableData.slice(totalsRowIndex + 1));
  
    const sortedRows = [...rowsWithoutTotal].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  
    // Add the totals row at the end of the sorted rows
    if (totalsRowIndex !== -1) {
      sortedRows.push(finalActivityTableData[totalsRowIndex]);
    }
  
    return sortedRows;
  }, [finalActivityTableData, sortConfig]);
  

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      let direction = "asc";
      if (prevConfig.key === key && prevConfig.direction === "asc") {
        direction = "desc";
      } else if (prevConfig.key === key && prevConfig.direction === "desc") {
        direction = null;
      }
      return { key, direction };
    });
  };

  const handleRequestAssignment = async (bountyId, bountyTitle) => {
    setRequestLoading(true);
    try {
      // Send the request to Firestore (adjust path/structure as needed)
      await db.collection("bounty-requests").add({
        bountyId,
         bountyTitle: bountyTitle, // Include bountyTitle
        requestedBy: `${userInfo.firstName} ${userInfo.lastName}`,
        status: "Pending",
        requestedAt: new Date(),
      });
  
      alert("Assignment requested successfully!");
    } catch (error) {
      console.error("Error requesting assignment:", error);
    } finally {
      setRequestLoading(false);
    }
  };
  

  // Helper function to render cell values, showing 0 instead of blank
  const renderCellValue = (value) => {
    return value || 0;
  };

  const menu = (
    <Menu>
      <Menu.Item key="1" onClick={fetchActivitySummary}>
        Activity Tracker
      </Menu.Item>
      <Menu.Item key="bountyBoard" onClick={handleBountyBoardOpen}>
        Bounty Board
      </Menu.Item>
      <Menu.Item key="quickSummary" onClick={() => fetchQuickSummary()}>
        Quick Summary
      </Menu.Item>
    </Menu>
  );

  const groupedColumns = [
    {
      title: (
        <span onClick={() => handleSort('editor')} style={{ cursor: 'pointer' }}>
          Editor {sortConfig.key === 'editor' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
        </span>
      ),
      dataIndex: 'editor',
      key: 'editor',
      fixed: 'left',
      width: 150,
    },
    {
      title: 'Added',
      children: [
        {
          title: (
            <span onClick={() => handleSort('New Player Profile Added')} style={{ cursor: 'pointer' }}>
              Player Profile {sortConfig.key === 'New Player Profile Added' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'New Player Profile Added',
          key: 'New Player Profile Added',
          className: styles.addedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('New General Report Added')} style={{ cursor: 'pointer' }}>
              General Report {sortConfig.key === 'New General Report Added' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'New General Report Added',
          key: 'New General Report Added',
          className: styles.addedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('New Scouting Report Added')} style={{ cursor: 'pointer' }}>
              Scouting Report {sortConfig.key === 'New Scouting Report Added' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'New Scouting Report Added',
          key: 'New Scouting Report Added',
          className: styles.addedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('New Intel Report Added')} style={{ cursor: 'pointer' }}>
              Intel Report {sortConfig.key === 'New Intel Report Added' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'New Intel Report Added',
          key: 'New Intel Report Added',
          className: styles.addedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('New Medical Report Added')} style={{ cursor: 'pointer' }}>
              Medical Report {sortConfig.key === 'New Medical Report Added' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'New Medical Report Added',
          key: 'New Medical Report Added',
          className: styles.addedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('New Background Overview Added')} style={{ cursor: 'pointer' }}>
              Background Overview {sortConfig.key === 'New Background Overview Added' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'New Background Overview Added',
          key: 'New Background Overview Added',
          className: styles.addedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('Scouting Overview Added')} style={{ cursor: 'pointer' }}>
              Scouting Overview {sortConfig.key === 'Scouting Overview Added' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'Scouting Overview Added',
          key: 'Scouting Overview Added',
          className: styles.addedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('New Measurement Added')} style={{ cursor: 'pointer' }}>
              Measurement {sortConfig.key === 'New Measurement Added' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'New Measurement Added',
          key: 'New Measurement Added',
          className: styles.addedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('New PI Score Added')} style={{ cursor: 'pointer' }}>
              PI Score {sortConfig.key === 'New PI Score Added' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'New PI Score Added',
          key: 'New PI Score Added',
          className: styles.addedRow,
          render: renderCellValue,
        },
      ],
    },
    {
      title: 'Edited',
      children: [
        {
          title: (
            <span onClick={() => handleSort('Player Profile was Edited')} style={{ cursor: 'pointer' }}>
              Player Profile {sortConfig.key === 'Player Profile was Edited' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'Player Profile was Edited',
          key: 'Player Profile was Edited',
          className: styles.editedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('General Report was Edited')} style={{ cursor: 'pointer' }}>
              General Report {sortConfig.key === 'General Report was Edited' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'General Report was Edited',
          key: 'General Report was Edited',
          className: styles.editedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('Scouting Report was Edited')} style={{ cursor: 'pointer' }}>
              Scouting Report {sortConfig.key === 'Scouting Report was Edited' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'Scouting Report was Edited',
          key: 'Scouting Report was Edited',
          className: styles.editedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('Intel Report was Edited')} style={{ cursor: 'pointer' }}>
              Intel Report {sortConfig.key === 'Intel Report was Edited' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'Intel Report was Edited',
          key: 'Intel Report was Edited',
          className: styles.editedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('Medical Report was Edited')} style={{ cursor: 'pointer' }}>
              Medical Report {sortConfig.key === 'Medical Report was Edited' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'Medical Report was Edited',
          key: 'Medical Report was Edited',
          className: styles.editedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('Background Overview Was Edited')} style={{ cursor: 'pointer' }}>
              Background Overview {sortConfig.key === 'Background Overview Was Edited' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'Background Overview Was Edited',
          key: 'Background Overview Was Edited',
          className: styles.editedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('Scouting Overview Was Edited')} style={{ cursor: 'pointer' }}>
              Scouting Overview {sortConfig.key === 'Scouting Overview Was Edited' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'Scouting Overview Was Edited',
          key: 'Scouting Overview Was Edited',
          className: styles.editedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('Measurement Edited')} style={{ cursor: 'pointer' }}>
              Measurement {sortConfig.key === 'Measurement Edited' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'Measurement Edited',
          key: 'Measurement Edited',
          className: styles.editedRow,
          render: renderCellValue,
        },
        {
          title: (
            <span onClick={() => handleSort('PI Score Edited')} style={{ cursor: 'pointer' }}>
              PI Score {sortConfig.key === 'PI Score Edited' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
            </span>
          ),
          dataIndex: 'PI Score Edited',
          key: 'PI Score Edited',
          className: styles.editedRow,
          render: renderCellValue,
        },
      ],
    },
    {
      title: (
        <span onClick={() => handleSort('totalEdits')} style={{ cursor: 'pointer' }}>
          Total Activity {sortConfig.key === 'totalEdits' && (sortConfig.direction === 'asc' ? '↑' : sortConfig.direction === 'desc' ? '↓' : '')}
        </span>
      ),
      dataIndex: 'totalEdits',
      key: 'totalEdits',
      render: renderCellValue,
    },
  ];
  return (
    <div className={styles.feed}>
      <div className={styles.topBar}>
        <p>Activity Feed</p>
        {windowWidth > 600 && (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight" className={styles.threeDotMenu}>
            <EllipsisOutlined className={styles.icon} />
          </Dropdown>
        )}
      </div>
      <div className={styles.tableContainer}>
        <table>
          <thead>
            <tr>
              <th>{windowWidth <= 600 ? 'Date & Time' : 'Date'}</th>
              {windowWidth > 600 && <th>Time</th>}
              <th>Player</th>
              <th>Type</th>
              <th>Editor</th>
            </tr>
          </thead>
          {loading ? (
            <tbody>
              {[...Array(5)].map((_, index) => (
                <tr key={index} className={styles.skeletonRow}>
                  <td colSpan="5">
                    <div className={styles.skeletonCell}></div>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              {activityFeed.map((item, index) => (
                <tr key={index}>
                  <td>
                    {windowWidth <= 600 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span>{moment.unix(item.timestamp).format("l")}</span>
                        <span style={{ fontSize: '12px' }}>{moment.unix(item.timestamp).format("hh:mm A")}</span>
                      </div>
                    ) : (
                      moment.unix(item.timestamp).format("l")
                    )}
                  </td>
                  {windowWidth > 600 && <td>{moment.unix(item.timestamp).format("hh:mm:ss A")}</td>}
                  <td>
                  {item.playerID ? (
                    <Link to={`/player-profile/detail/${item.playerID}`}>
                      {item.player}
                    </Link>
                  ) : playerIdMap[item.player] ? (
                    <Link to={`/player-profile/detail/${playerIdMap[item.player]}`}>
                      {item.player}
                    </Link>
                  ) : (
                    item.player
                  )}
                  </td>
                  <td>{item.desc.replace(/\bwas\b/gi, '').replace(/\s+/g, ' ').trim()}</td>
                  <td>{item.editor}</td>
                </tr>
              ))}
            </tbody>
          )}
        </table>

        <div className={styles.centeredButton}>
          {loadingMore ? (
            <p>Loading more...</p>
          ) : (
            <button onClick={loadMore}>Load More</button>
          )}
        </div>
      </div>

       {/* Activity Tracker Summary Modal */}
       <Modal
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
              <span>Activity Tracker Summary</span>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontWeight: "bold", fontSize: "14px" }}>Select Date Range:</span>
                <Dropdown
                  menu={{
                    items: [
                      ...Object.keys(getPresetRanges()).map(preset => ({
                        key: preset,
                        label: preset,
                        onClick: () => handleActivityPresetClick(preset)
                      })),
                      {
                        key: "custom",
                        label: "Custom Range",
                        onClick: () => handleActivityPresetClick("Custom Range")
                      }
                    ]
                  }}
                  trigger={["click"]}
                  placement="bottomLeft"
                >
                  <Button size="small">
                    {activitySelectedRangeType === "custom" ? "Custom Range" : 
                     activitySelectedRangeType === "today" ? "Today" :
                     activitySelectedRangeType === "yesterday" ? "Yesterday" :
                     activitySelectedRangeType === "currentweek" ? "Current Week" :
                     activitySelectedRangeType === "currentmonth" ? "Current Month" :
                     activitySelectedRangeType === "previousmonth" ? "Previous Month" : "Select Range"}
                  </Button>
                </Dropdown>
                {activitySelectedRangeType === "custom" && (
                  <RangePicker
                    value={activityDateRange}
                    onChange={handleActivityDateRangeChange}
                    format="MM-DD-YYYY"
                    placeholder={['Start Date', 'End Date']}
                    allowClear={true}
                    showTime={false}
                    mode={['date', 'date']}
                    disabledDate={(current) => {
                      // Disable future dates
                      return current && current > moment().endOf('day');
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        }
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={1000}
        bodyStyle={{ padding: 0 }}
      >
        {loadingSummary ? (
          <div className={styles.spinnerContainer}>
            <Spin
              size="large"
              indicator={<LoadingOutlined style={{ fontSize: 60, color: "black" }} spin />}
            />
          </div>
        ) : (
          <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table
              columns={groupedColumns}
              dataSource={sortedData}
              pagination={false}
              rowKey="key"
              size="middle"
              scroll={{ x: 'max-content' }}
            />
          </div>
        )}
      </Modal>

      {/* Bounty Board Modal */}
      <Modal
        title="Bounty Board"
        visible={bountyModalVisible}
        onCancel={() => setBountyModalVisible(false)}
        footer={null}
        width={1000}
        bodyStyle={{ padding: 0 }}
      >
        {loading ? (
          <div className={styles.spinnerContainer}>
            <Spin
              size="large"
              indicator={<LoadingOutlined style={{ fontSize: 60, color: "black" }} spin />}
            />
          </div>
        ) : (
          <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Bounty</th>
                  <th>Description</th>
                  <th>Reward</th>
                  <th>Assigned Editor</th>
                  <th>Progress</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bountyData.map((bounty) => (
                  <tr
                    key={bounty.id}
                    className={
                      bounty.progress === "Completed" ? styles.completedRow : ""
                    }
                  >
                    <td>{bounty.title}</td>
                    <td>
                      {bounty.description.length > 100 ? (
                        <>
                          {bounty.description.slice(0, 100)}...
                          <span
                            className={styles.readMore}
                            onClick={() => setDescriptionModalData(bounty)}
                          >
                            Read more
                          </span>
                        </>
                      ) : (
                        bounty.description
                      )}
                    </td>
                    <td>{bounty.reward}</td>
                    <td>
                    {bounty.assignedEditor ? (
                      bounty.assignedEditor
                    ) : bounty.progress === "Completed" ? (
                      <span className={`${styles.requestLink} ${styles.disabled}`}>
                        Request Assignment
                      </span>
                    ) : (
                      <span
                        className={styles.requestLink}
                        onClick={() => handleRequestAssignment(bounty.id, bounty.title)}
                        disabled={requestLoading}
                      >
                        Request Assignment
                      </span>
                    )}
                    </td>
                    <td>{bounty.progress || "Not Started"}</td>
                    <td className={styles.iconCell}>
                    <FaEye
                      className={styles.viewIcon}
                      onClick={() => setViewModalData(bounty)}
                    />
                  </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Read More Modal */}
            {descriptionModalData && (
              <div className={styles.modalContainer}>
                <div className={styles.modalContent}>
                  <h4>Full Description</h4>
                  <p style={{ whiteSpace: "pre-wrap" }}>
                    {descriptionModalData.description}
                  </p>
                  <button onClick={() => setDescriptionModalData(null)}>Close</button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <span>Quick Summary</span>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontWeight: "bold", fontSize: "14px" }}>Select Date Range:</span>
              <Dropdown
                menu={{
                  items: [
                    ...Object.keys(getPresetRanges()).map(preset => ({
                      key: preset,
                      label: preset,
                      onClick: () => handlePresetClick(preset)
                    })),
                    {
                      key: "custom",
                      label: "Custom Range",
                      onClick: () => handlePresetClick("Custom Range")
                    }
                  ]
                }}
                trigger={["click"]}
                placement="bottomLeft"
              >
                <Button size="small">
                  {selectedRangeType === "custom" ? "Custom Range" : 
                   selectedRangeType === "today" ? "Today" :
                   selectedRangeType === "yesterday" ? "Yesterday" :
                   selectedRangeType === "currentweek" ? "Current Week" :
                   selectedRangeType === "currentmonth" ? "Current Month" :
                   selectedRangeType === "previousmonth" ? "Previous Month" : "Select Range"}
                </Button>
              </Dropdown>
              {selectedRangeType === "custom" && (
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="MM-DD-YYYY"
                  placeholder={['Start Date', 'End Date']}
                  allowClear={true}
                  showTime={false}
                  mode={['date', 'date']}
                  disabledDate={(current) => {
                    // Disable future dates
                    return current && current > moment().endOf('day');
                  }}
                  size="small"
                />
              )}
            </div>
          </div>
        </div>
      }
      visible={quickSummaryModalVisible}
      onCancel={() => setQuickSummaryModalVisible(false)}
      footer={null}
      width={800}
    >
      {loadingQuickSummary ? (
        <div className={styles.spinnerContainer}>
          <Spin
            size="large"
            indicator={<LoadingOutlined style={{ fontSize: 60, color: "black" }} spin />}
          />
        </div>
      ) : (
        quickSummaryData && (
          <div className={styles.summaryContainer}>
            <div className={styles.summarySection}>
              <h3>{rangeHeaders.primary || "Current Week Breakdown"}</h3>
              <table className={styles.summaryTable}>
                <tbody>
                  <tr><td>Profiles Added</td><td>{quickSummaryData.primary.profilesAdded}</td></tr>
                  <tr><td>Profiles Edited</td><td>{quickSummaryData.primary.profilesEdited}</td></tr>
                  <tr><td>Scouting Reports Added</td><td>{quickSummaryData.primary.scoutingReportsAdded}</td></tr>
                  <tr><td>Scouting Reports Edited</td><td>{quickSummaryData.primary.scoutingReportsEdited || 0}</td></tr>
                  <tr><td>Intel Reports Added</td><td>{quickSummaryData.primary.intelReportsAdded}</td></tr>
                  <tr><td>Intel Reports Edited</td><td>{quickSummaryData.primary.intelReportsEdited || 0}</td></tr>
                  <tr><td>Medical Reports Added</td><td>{quickSummaryData.primary.medicalReportsAdded}</td></tr>
                  <tr><td>Medical Reports Edited</td><td>{quickSummaryData.primary.medicalReportsEdited || 0}</td></tr>
                  <tr><td>General Reports Added</td><td>{quickSummaryData.primary.generalReportsAdded}</td></tr>
                  <tr><td>General Reports Edited</td><td>{quickSummaryData.primary.generalReportsEdited || 0}</td></tr>
                  <tr><td>Background Overviews Added</td><td>{quickSummaryData.primary.backgroundOverviewsAdded}</td></tr>
                  <tr><td>Background Overviews Edited</td><td>{quickSummaryData.primary.backgroundOverviewsEdited}</td></tr>
                  <tr><td>Scouting Overviews Added</td><td>{quickSummaryData.primary.scoutingOverviewsAdded}</td></tr>
                  <tr><td>Scouting Overviews Edited</td><td>{quickSummaryData.primary.scoutingOverviewsEdited}</td></tr>
                  <tr><td>PI Scores Added</td><td>{quickSummaryData.primary.piScoresAdded}</td></tr>
                  <tr><td>PI Scores Edited</td><td>{quickSummaryData.primary.piScoresEdited}</td></tr>
                  <tr className={styles.totalRow}><td>Total DB Additions</td><td>{quickSummaryData.primary.totalAdditions}</td></tr>
                  <tr className={styles.totalRow}><td>Total DB Edits</td><td>{quickSummaryData.primary.totalEdits}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </Modal>
   
    {/* View Modal */}
    {viewModalData && (
        <Modal
          title={viewModalData.title}
          visible={!!viewModalData}
          onCancel={() => setViewModalData(null)}
          footer={null}
        >
          <p>
            <strong>Description:</strong>
          </p>
          <p style={{ whiteSpace: "pre-wrap" }}>{viewModalData.description}</p>
          <p>
            <strong>Reward:</strong> {viewModalData.reward}
          </p>
          <p>
            <strong>Assigned Editor:</strong>{" "}
            {viewModalData.assignedEditor || "Unassigned"}
          </p>
          <p>
            <strong>Progress:</strong> {viewModalData.progress || "Not Started"}
          </p>
        </Modal>
      )}
   
   
    </div>
  );
}
